import { FC, useEffect } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useSetAtom } from "jotai";

import {
  CleanupTypeEnum,
  ServiceTaskTypeEnum,
  TaskDriverStatusEnum,
} from "@/domain/models/common";
import { ServicesType } from "@/domain/models/job";
import { useTranslator } from "@/i18n/useTranslator";

import { DriverFlowBottomButton } from "../blocks";
import { RightPanel } from "../components/RightPanel";
import { TaskNotes } from "../components/task/Notes";
import { DsButton, DsDataDisplay, DsDivider, DsText } from "../components/ui";
import { useUpdateJobMutation } from "../data/mutations/jobMutations";
import { useUpdateServiceMutation } from "../data/mutations/serviceMutations";
import {
  useCompleteTaskMutation,
  useUpdateTaskMutation,
} from "../data/mutations/taskMutations";
import { useJobQuery } from "../data/queries/jobQueries";
import {
  ServiceQueryKeys,
  useServiceQuery,
} from "../data/queries/serviceQueries";
import { TaskQueryKeys, useTaskQuery } from "../data/queries/taskQueries";
import { JobServiceTaskBaseType } from "../domain/task";
import {
  isDriverReviewingJunkLoadAtom,
  isDriverReviewingTaskAtom,
} from "../stores/atoms";
import { setJunkLoadInProgress } from "../utils/service";
import { formatCleanupType } from "../utils/strings";
import { checkTaskHasCleanupType } from "../utils/task";
import { CustomerNameText } from "./CustomerNameText";
import { DriverTaskPicture } from "./DriverTaskPicture";
import { DriverTaskReviewJunkSection } from "./DriverTaskReviewJunkSection";
import { DriverTopActionButton } from "./DriverTopActionButton";

type DriverTaskReviewScreenPropsType = JobServiceTaskBaseType & {
  onBottomButtonPress?: () => void;
};

const DriverTaskReviewScreen: FC<DriverTaskReviewScreenPropsType> = ({
  jobId,
  serviceId,
  taskId,
  onBottomButtonPress,
}) => {
  const { t } = useTranslator();

  const queryClient = useQueryClient();

  const setIsDriverReviewingTask = useSetAtom(isDriverReviewingTaskAtom);
  const setDriverIsReviewingJunkLoad = useSetAtom(
    isDriverReviewingJunkLoadAtom,
  );

  const { mutate: completeTask, isPending: isCompletingTask } =
    useCompleteTaskMutation();

  const { data: jobData } = useJobQuery({ jobId });
  const { data: taskData } = useTaskQuery({ jobId, serviceId, taskId });
  const { data: serviceData } = useServiceQuery({ jobId, serviceId });

  const {
    mutate: updateTask,
    isPending: isUpdatingTask,
    variables: updateTaskVariables,
  } = useUpdateTaskMutation();

  const { mutate: updateService, isPending: isUpdatingService } =
    useUpdateServiceMutation();

  const { mutate: updateJob, isPending: isUpdatingJob } =
    useUpdateJobMutation();

  useEffect(() => {
    setIsDriverReviewingTask(true);
  }, [setIsDriverReviewingTask]);

  if (!jobData || !taskData) {
    return null;
  }

  const hasJobMultipleLoads = Boolean(serviceData?.junkLoads?.length);

  const onCompleteJob = async () => {
    const promises = [];
    const shouldGoToDump =
      (taskId === ServiceTaskTypeEnum.JUNKREMOVAL ||
        taskId === ServiceTaskTypeEnum.EXCHANGEBIN ||
        taskId === ServiceTaskTypeEnum.PICKUPBIN ||
        taskId === ServiceTaskTypeEnum.WAITANDLOADBIN) &&
      !taskData.hasDumped;

    if (shouldGoToDump) {
      const servicesParams: Partial<ServicesType> = {};

      // If driver is performing multiple loads, set the last load to in progress to be catched by the dump flow
      if (hasJobMultipleLoads && serviceData?.junkLoads) {
        const updatedJunkLoads = setJunkLoadInProgress(
          serviceData,
          serviceData.junkLoads.length - 1,
        );

        if (updatedJunkLoads) {
          servicesParams.junkLoads = updatedJunkLoads;
        }
      }

      promises.push(
        updateService({
          jobId,
          serviceId,
          needToDump: true,
          hasDumped: false,
          driverFlowStatus: TaskDriverStatusEnum.DRIVING_TO_DUMP,
          ...servicesParams,
        }),
      );

      promises.push(
        updateTask({
          jobId,
          serviceId,
          taskId,
          needToDump: true,
          hasDumped: false,
          driverFlowStatus: TaskDriverStatusEnum.DRIVING_TO_DUMP,
        }),
      );

      promises.push(
        queryClient.invalidateQueries({ queryKey: TaskQueryKeys.all }),
        queryClient.invalidateQueries({ queryKey: ServiceQueryKeys.all }),
      );
    } else {
      promises.push(
        completeTask({
          jobId,
          serviceId,
          taskId,
        }),
      );
    }

    await Promise.allSettled(promises);
    setIsDriverReviewingTask(false);
    setDriverIsReviewingJunkLoad(false);

    onBottomButtonPress?.();
  };

  const goBackTo = (driverFlowStatus: TaskDriverStatusEnum) => {
    updateService({
      jobId,
      serviceId,
      driverFlowStatus,
    });

    updateTask({
      jobId,
      serviceId,
      taskId,
      driverFlowStatus,
    });
  };

  const isCompleting =
    isCompletingTask ||
    (isUpdatingService &&
      isUpdatingTask &&
      isUpdatingJob &&
      updateTaskVariables.needToDump === true);

  const isJunkTask = taskId === ServiceTaskTypeEnum.JUNKREMOVAL;
  const hasCleanupType = checkTaskHasCleanupType(taskData);

  const isExchangeBinTask = taskId === ServiceTaskTypeEnum.EXCHANGEBIN;

  const isDropOrExchangeBinTask =
    taskId === ServiceTaskTypeEnum.DROPBIN || isExchangeBinTask;

  const isPickUpBinTask = taskId === ServiceTaskTypeEnum.PICKUPBIN;
  const isWaitAndLoadBinTask = taskId === ServiceTaskTypeEnum.WAITANDLOADBIN;
  const isDumpAndReturnBinTask =
    taskId === ServiceTaskTypeEnum.DUMPANDRETURNBIN;

  // Can complete job if it's a junk task of if it's a bin task and there's no turnaround data
  // If there's turnaround data, means driver couldn't drop the bin
  const canCompleteJob = isJunkTask || !serviceData?.turnaroundPhoto;

  const hasTurnaroundPhoto = Boolean(serviceData?.turnaroundPhoto);

  return (
    <>
      <div className="flex flex-col h-full w-full">
        <div className="flex flex-grow w-full flex-col">
          <DsText variant="pageTitle">
            {t("driver_task_review_screen.title")}
          </DsText>
          <DsDivider className="my-4" />

          <DriverTopActionButton
            jobId={jobId}
            serviceId={serviceId}
            taskId={taskId}
          />

          <DsDivider className="mt-4" />

          <DsDataDisplay
            title="driver_task_details.customer"
            data={
              <CustomerNameText
                jobId={jobId}
                serviceId={serviceId}
                variant="dataDisplayValue"
                className="font-bold"
              />
            }
          />

          {isDropOrExchangeBinTask && (
            <>
              <DsDataDisplay
                title="driver_task_review_screen.can_drop"
                data={serviceData?.siteDropPhoto ? t("yes") : t("no")}
                onEditClick={() =>
                  goBackTo(TaskDriverStatusEnum.ASKING_CAN_DROP_BIN)
                }
                isLoading={Boolean(
                  updateTaskVariables?.driverFlowStatus ===
                    TaskDriverStatusEnum.ASKING_CAN_DROP_BIN,
                )}
              />

              {serviceData?.assignedBin && (
                <DsDataDisplay
                  title="driver_task_review_screen.dropped_bin"
                  data={`${serviceData?.assignedBin?.identifier} (${serviceData?.assignedBin?.size} ${t("yard")})`}
                  onEditClick={() =>
                    goBackTo(TaskDriverStatusEnum.SELECTING_BIN_FOR_SERVICE)
                  }
                  isLoading={Boolean(
                    updateTaskVariables?.driverFlowStatus ===
                      TaskDriverStatusEnum.SELECTING_BIN_FOR_SERVICE,
                  )}
                />
              )}

              {serviceData?.siteDropPhoto && (
                <DsDataDisplay
                  title="driver_task_review_screen.dropped_bin"
                  dataClassName="flex"
                  data={
                    <DriverTaskPicture
                      field="siteDropPhoto"
                      jobId={jobId}
                      serviceId={serviceId}
                    />
                  }
                />
              )}

              {isExchangeBinTask && (
                <>
                  <DsDataDisplay
                    title="driver_task_review_screen.exchanged_bin"
                    data={`${serviceData?.connectedTask?.assignedBin?.identifier} (${serviceData?.connectedTask?.assignedBin?.size} ${t("yard")})`}
                  />
                  <DsDataDisplay
                    title="driver_task_review_screen.exchanged_bin"
                    dataClassName="flex"
                    data={
                      <DriverTaskPicture
                        field="binMaterialsPhoto"
                        jobId={jobId}
                        serviceId={serviceId}
                      />
                    }
                  />
                  <DsDataDisplay
                    title="driver_task_review_screen.contents"
                    data={serviceData?.binMaterials}
                    onEditClick={() =>
                      goBackTo(TaskDriverStatusEnum.CATEGORIZE_BIN_CONTENTS)
                    }
                    isLoading={Boolean(
                      updateTaskVariables?.driverFlowStatus ===
                        TaskDriverStatusEnum.CATEGORIZE_BIN_CONTENTS,
                    )}
                  />
                </>
              )}

              {serviceData?.turnaroundPhoto && (
                <>
                  <DsDataDisplay
                    title="driver_task_review_screen.reason"
                    data={serviceData?.turnaroundPhoto?.comment}
                    onEditClick={() =>
                      goBackTo(
                        TaskDriverStatusEnum.FILLING_REASON_CANT_DROP_BIN,
                      )
                    }
                    isLoading={Boolean(
                      updateTaskVariables?.driverFlowStatus ===
                        TaskDriverStatusEnum.FILLING_REASON_CANT_DROP_BIN,
                    )}
                  />

                  <DsDataDisplay
                    title="driver_task_review_screen.obstruction"
                    dataClassName="flex"
                    data={
                      <DriverTaskPicture
                        field="turnaroundPhoto"
                        jobId={jobId}
                        serviceId={serviceId}
                      />
                    }
                  />
                </>
              )}
            </>
          )}

          {isPickUpBinTask && (
            <DsDataDisplay
              title="driver_task_details.bin"
              data={`${serviceData?.connectedTask?.assignedBin?.identifier} (${serviceData?.connectedTask?.assignedBin?.size} ${t("yard")})`}
            />
          )}

          {isWaitAndLoadBinTask && (
            <DsDataDisplay
              title="driver_task_details.bin"
              data={`${serviceData?.assignedBin?.identifier} (${serviceData?.assignedBin?.size} ${t("yard")})`}
            />
          )}

          {(isPickUpBinTask || isWaitAndLoadBinTask) && (
            <>
              <DsDataDisplay
                title="driver_task_review_screen.contents"
                data={serviceData?.binMaterials}
                onEditClick={() =>
                  goBackTo(TaskDriverStatusEnum.CATEGORIZE_BIN_CONTENTS)
                }
                isLoading={Boolean(
                  updateTaskVariables?.driverFlowStatus ===
                    TaskDriverStatusEnum.CATEGORIZE_BIN_CONTENTS,
                )}
              />
              <DsDataDisplay
                title="driver_task_review_screen.bin_contents"
                dataClassName="flex"
                data={
                  <DriverTaskPicture
                    field="binMaterialsPhoto"
                    jobId={jobId}
                    serviceId={serviceId}
                  />
                }
              />
            </>
          )}

          {isDumpAndReturnBinTask && (
            <>
              <DsDataDisplay
                title="driver_task_review_screen.bin_contents"
                dataClassName="flex"
                data={
                  <DriverTaskPicture
                    field="binMaterialsPhoto"
                    jobId={jobId}
                    serviceId={serviceId}
                  />
                }
              />
              <DsDataDisplay
                title="driver_dump_tasks_review_screen.surcharge"
                dataClassName="font-bold"
                data={serviceData?.weightSurcharge?.toString()}
                onEditClick={() =>
                  goBackTo(TaskDriverStatusEnum.FILLING_WEIGHT_SURCHARGE)
                }
                isLoading={Boolean(
                  updateTaskVariables?.driverFlowStatus ===
                    TaskDriverStatusEnum.FILLING_WEIGHT_SURCHARGE,
                )}
              />
              <DsDataDisplay
                title="driver_dump_tasks_review_screen.dump_receipt"
                dataClassName="flex"
                data={
                  <DriverTaskPicture
                    field="dumpReceiptPhotoUrl"
                    jobId={jobId}
                    serviceId={serviceId}
                  />
                }
              />
              <DsDataDisplay
                title="driver_task_review_screen.returned_bin"
                dataClassName="flex"
                data={
                  <DriverTaskPicture
                    field="siteDropPhoto"
                    jobId={jobId}
                    serviceId={serviceId}
                  />
                }
              />
            </>
          )}

          {isJunkTask && (
            <>
              {hasJobMultipleLoads ? (
                <>
                  {serviceData?.junkLoads?.map((_, index) => (
                    <DriverTaskReviewJunkSection
                      key={index}
                      jobId={jobId}
                      serviceId={serviceId}
                      taskId={taskId}
                      junkLoadIndex={index}
                    />
                  ))}
                </>
              ) : (
                <DriverTaskReviewJunkSection
                  jobId={jobId}
                  serviceId={serviceId}
                  taskId={taskId}
                />
              )}

              <DsDataDisplay
                title="driver_task_review_screen.interior_cleanup_type"
                dataClassName="font-bold"
                data={formatCleanupType({
                  cleanupType: taskData?.cleanupType,
                  shouldShowNoTypeAsNone: true,
                })}
                onEditClick={() =>
                  goBackTo(TaskDriverStatusEnum.FILLING_INTERIOR_CLEANUP_TYPE)
                }
                isLoading={Boolean(
                  updateTaskVariables?.driverFlowStatus ===
                    TaskDriverStatusEnum.FILLING_INTERIOR_CLEANUP_TYPE,
                )}
              />
            </>
          )}
          {Boolean(
            isJunkTask && taskData?.cleanupType === CleanupTypeEnum.FULLDETAIL,
          ) && (
            <DsDataDisplay
              title="driver_task_review_screen.sqft_cleaned"
              dataClassName="font-bold"
              data={serviceData?.sqft?.toString()}
              onEditClick={() =>
                goBackTo(TaskDriverStatusEnum.FILLING_SQFT_CLEANED)
              }
              isLoading={Boolean(
                updateTaskVariables?.driverFlowStatus ===
                  TaskDriverStatusEnum.FILLING_SQFT_CLEANED,
              )}
            />
          )}
          {Boolean(
            isJunkTask &&
              hasCleanupType &&
              taskData?.cleanupType !== CleanupTypeEnum.FULLDETAIL,
          ) && (
            <DsDataDisplay
              title="driver_task_review_screen.number_floors_cleaned"
              dataClassName="font-bold"
              data={serviceData?.floorsCleaned?.toString()}
              onEditClick={() =>
                goBackTo(TaskDriverStatusEnum.FILLING_FLOORS_CLEANED)
              }
              isLoading={Boolean(
                updateTaskVariables?.driverFlowStatus ===
                  TaskDriverStatusEnum.FILLING_FLOORS_CLEANED,
              )}
            />
          )}
          {Boolean(
            isJunkTask &&
              hasCleanupType &&
              taskData?.cleanupType !== CleanupTypeEnum.FULLDETAIL,
          ) && (
            <DsDataDisplay
              title="driver_task_review_screen.sqft_per_floor"
              dataClassName="font-bold"
              data={serviceData?.floorSqft?.toString()}
              onEditClick={() =>
                goBackTo(TaskDriverStatusEnum.FILLING_SQFT_PER_FLOOR)
              }
              isLoading={Boolean(
                updateTaskVariables?.driverFlowStatus ===
                  TaskDriverStatusEnum.FILLING_SQFT_PER_FLOOR,
              )}
            />
          )}
          {isJunkTask && (
            <DsDataDisplay
              title="driver_task_review_screen.area_cleaned"
              dataClassName="flex"
              data={
                <DriverTaskPicture
                  field="cleanedAreasPhotos"
                  jobId={jobId}
                  serviceId={serviceId}
                />
              }
            />
          )}
          <RightPanel
            title={t("driver_task_review_screen.task_notes")}
            content={
              <TaskNotes jobId={jobId} serviceId={serviceId} taskId={taskId} />
            }>
            <DsButton className="my-4 w-full">
              {t("driver_task_review_screen.add_post_job_comments")}
            </DsButton>
          </RightPanel>
          <DsDivider />
        </div>
        {!canCompleteJob && (
          <DsText>{t("driver_task_review_screen.obstruction_message")}</DsText>
        )}
        <DriverFlowBottomButton
          nextButtonTitle={
            canCompleteJob
              ? "driver_task_review_screen.complete_job"
              : "driver_task_review_screen.back_to_list"
          }
          isNextButtonLoading={isCompleting}
          onNextButtonClick={
            canCompleteJob
              ? () => {
                  void onCompleteJob();
                }
              : () => {
                  if (hasTurnaroundPhoto) {
                    updateService({
                      jobId,
                      serviceId,
                      assignedTo: null,
                    });
                    updateJob({
                      jobId,
                      assignedTo: null,
                    });
                    updateTask({
                      jobId,
                      taskId,
                      serviceId,
                      assignedTo: null,
                    });
                  }
                  onBottomButtonPress?.();
                }
          }
        />
      </div>
    </>
  );
};

export { DriverTaskReviewScreen };
